/**
 * FDV — GrowthBook (experimentos + feature flags). Ver docs/migration/13-cro-analytics-tracking.md §6.
 *
 * ⚠️ STUB — la infra self-hosted (`growthbook.flordevenus.com.mx`) y el warehouse todavía
 * no existen. Con `clientKey` vacío el snippet NO carga el SDK de 53 KB; este archivo
 * (~2 KB) igual expone una API no-op en `window.FDV` para que el código de Fase 2 pueda
 * referenciarla. Deja listo:
 *   - SDK autoalojado condicional (`assets/growthbook-sdk.js` v1.7.0, `window.growthbook.GrowthBook`)
 *   - atributos de targeting desde Liquid + JS (§6)
 *   - `trackingCallback` → `window.FDV.track('fdv_experiment_viewed', …)` (bus → gateway/warehouse)
 *   - API en `window.FDV`: `growthbook`, `feature(k)`, `isOn(k)`, `getFeatureValue(k, d)`,
 *     y la promesa `FDV.gbReady` (resuelve a `null` si el SDK no está)
 *
 * SIN anti-flicker (el body no se oculta). El anti-flicker de GrowthBook solo se aplicaría,
 * por plantilla, a un test visual above-the-fold (hero / precio / botón de compra) — §6.
 * Gateado por consent (`FDV.consent.analytics`).
 */
(() => {
  const cfg = readConfig();
  const FDV = (window.FDV = window.FDV || {});
  if (FDV.growthbook) return;

  let resolveReady;
  let done = false;
  FDV.gbReady = new Promise((r) => (resolveReady = r));
  FDV.feature = (k) => (FDV.growthbook ? FDV.growthbook.evalFeature(k) : { value: null, on: false, off: true, source: 'unknownFeature' });
  FDV.isOn = (k) => (FDV.growthbook ? FDV.growthbook.isOn(k) : false);
  FDV.getFeatureValue = (k, dflt) => (FDV.growthbook ? FDV.growthbook.getFeatureValue(k, dflt) : dflt);

  withFDV((bus) => {
    if (bus.consent?.analytics) init(bus);
    else {
      document.addEventListener(
        'fdv:consent',
        (e) => {
          if (e.detail?.analytics) init(bus);
        },
        { once: true }
      );
    }
  });

  function init(bus) {
    if (done) return;
    done = true;

    const GrowthBook = window.growthbook && window.growthbook.GrowthBook;
    if (typeof GrowthBook !== 'function') {
      resolveReady(null);
      return;
    }

    const gb = new GrowthBook({
      apiHost: cfg.apiHost || undefined,
      clientKey: cfg.clientKey || undefined,
      enableDevMode: cfg.debug === true,
      subscribeToChanges: false,
      attributes: buildAttributes(bus),
      trackingCallback: (experiment, result) => {
        try {
          bus.track('fdv_experiment_viewed', {
            experiment_id: experiment.key,
            variation_id: String(result.variationId ?? result.key ?? ''),
            variation_name: result.name || null,
          });
        } catch (e) {
          /* noop */
        }
      },
    });

    FDV.growthbook = gb;
    document.dispatchEvent(new CustomEvent('fdv:growthbook-ready', { detail: gb }));

    // STUB: sin `clientKey` no se pide nada al backend (evita un fetch colgado a un host
    // inexistente). El SDK queda operativo con 0 features → todo flag/experimento devuelve
    // su default. Al tener el SDK endpoint real, `clientKey` en el config activa esto.
    if (!cfg.clientKey) {
      resolveReady(gb);
      return;
    }
    const started = gb.init ? gb.init({ streaming: false }) : gb.loadFeatures();
    Promise.resolve(started)
      .catch(() => {})
      .finally(() => resolveReady(gb));
  }

  function buildAttributes(bus) {
    const params = new URLSearchParams(location.search);
    let device = 'desktop';
    try {
      if (window.matchMedia('(max-width: 749px)').matches) device = 'mobile';
    } catch (e) {
      /* noop */
    }
    return {
      id: bus.anonId || '',
      loggedIn: cfg.loggedIn === true,
      deviceType: device,
      country: cfg.country || 'MX',
      url: location.href,
      pageType: cfg.page_type || null,
      template: cfg.template || null,
      utmSource: params.get('utm_source'),
      utmMedium: params.get('utm_medium'),
      utmCampaign: params.get('utm_campaign'),
    };
  }

  // =====================================================================================
  function readConfig() {
    try {
      return JSON.parse(document.getElementById('fdv-growthbook-config')?.textContent || '{}');
    } catch (e) {
      return {};
    }
  }

  function withFDV(cb, tries = 40) {
    if (window.FDV && window.FDV.track) return cb(window.FDV);
    if (tries <= 0) return;
    setTimeout(() => withFDV(cb, tries - 1), 50);
  }
})();
